import { ADMIN_APPEAL_PRIORITIES, ADMIN_APPEAL_STATUSES } from '@carcheck/shared';
import { z } from 'zod';

import type { StatusOption } from '../../../components/filters/StatusMultiSelect';

/**
 * URL filter schema for the appeals moderation list (AUT-130). Mirrors
 * the backend `ListAdminAppealsQuerySchema` — `status` is a multi-select
 * over the LIVE 3-state `ADMIN_APPEAL_STATUSES` (never the dead
 * `approved` / `rejected`), `priority` is a multi-select over the full
 * 3-value `ADMIN_APPEAL_PRIORITIES`, and `dateFrom` / `dateTo` are ISO
 * date strings binding the backend's `createdAt` filter.
 *
 * NO reason filter — the api does not filter on reason (architect 🟠 #6);
 * the reason chip renders in the grid but is not a facet. NO free-text
 * search — the appeals list endpoint has no `q` param.
 *
 * Both multi-selects default to `[]` (show all live rows) — appeals have
 * no single obvious triage state (the queue mixes `pending` /
 * `under_review` / `resolved`), so the SPA shows everything and lets the
 * operator narrow. Every key has a `.catch` default so a malformed /
 * absent query param degrades safely instead of throwing.
 */

const statusSchema = z
  .string()
  .optional()
  .catch(undefined)
  .transform((raw) => {
    if (raw === undefined) return [] as (typeof ADMIN_APPEAL_STATUSES)[number][];
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is (typeof ADMIN_APPEAL_STATUSES)[number] =>
        (ADMIN_APPEAL_STATUSES as readonly string[]).includes(s),
      );
  });

const prioritySchema = z
  .string()
  .optional()
  .catch(undefined)
  .transform((raw) => {
    if (raw === undefined) return [] as (typeof ADMIN_APPEAL_PRIORITIES)[number][];
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is (typeof ADMIN_APPEAL_PRIORITIES)[number] =>
        (ADMIN_APPEAL_PRIORITIES as readonly string[]).includes(s),
      );
  });

const dateSchema = z.string().optional().catch(undefined);

export const appealFiltersSchema = z.object({
  status: statusSchema,
  priority: prioritySchema,
  dateFrom: dateSchema,
  dateTo: dateSchema,
});

export type AppealFilters = z.infer<typeof appealFiltersSchema>;

/** Status filter options (multi-select) — labels resolved via i18n keys. */
export const STATUS_OPTIONS: StatusOption[] = ADMIN_APPEAL_STATUSES.map((value) => ({
  value,
  labelKey: `appeals.status.${value}`,
}));

/** Priority filter options (multi-select) — labels resolved via i18n keys. */
export const PRIORITY_OPTIONS: StatusOption[] = ADMIN_APPEAL_PRIORITIES.map((value) => ({
  value,
  labelKey: `appeals.priority.${value}`,
}));

/** True when any managed filter is active (drives the Clear-filters link). */
export const hasActiveFilters = (filters: AppealFilters): boolean =>
  (filters.status?.length ?? 0) > 0 ||
  (filters.priority?.length ?? 0) > 0 ||
  Boolean(filters.dateFrom && filters.dateFrom.length > 0) ||
  Boolean(filters.dateTo && filters.dateTo.length > 0);
